import { reinitFlowbiteWidgets } from '../utils/ui/flowbite-reinit.js';

/**
 * CODA detail — Billing / Bank statement reconciliation workspace (P5b)
 *
 * Sister module to `coda-upload.js`. Same shape, different entity:
 *
 *   - List polling  → Lines polling
 *     The lines table carries `data-poll-needed="1"` while any line is
 *     still non-terminal (suggested / unmatched / pending). The JS
 *     fetches the fragment URL, swaps `#coda-lines` innerHTML, and
 *     stops once the server drops the attribute. Identical contract
 *     to the upload list; code is duplicated rather than unified
 *     because the two endpoints live under different ids and we want
 *     the modules to stay independently loadable.
 *
 *   - Upload drawer → Line drawer
 *     Clicking any row (with `data-coda-line-open` + `data-url`) fetches
 *     the line drawer fragment and injects it into the drawer shell,
 *     exactly like the upload drawer. The drawer's Confirm / Ignore
 *     POSTs go through HTMX natively (hx-post + hx-target), and the
 *     server sends an `HX-Trigger: closeCodaLineDrawer` header — we
 *     listen for that event on document.body and close the drawer.
 *
 *   Why two modules not one: `coda-upload.js` is loaded on the CODA
 *   list page, `coda-detail.js` on the detail page. They never live
 *   together, so sharing state / selectors would be more coupling
 *   than reuse.
 */

var POLL_INTERVAL_MS = 3000;

function findClosest(el, selector) {
    var node = (el && el.nodeType === 1) ? el : (el && el.parentElement) || null;
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest(selector);
}

function syncDataAttrs(target, source) {
    // Mirror of `coda-upload.js#syncDataAttrs` — see that file for the
    // full rationale. TL;DR: keep the #coda-lines wrapper node
    // identity (avoids flicker) but copy every server-driven
    // data-* attribute onto it so `data-poll-needed` actually
    // reflects the current server state after each swap.
    if (!target || !source) return;
    var i;
    var seen = {};
    for (i = 0; i < source.attributes.length; i++) {
        var a = source.attributes[i];
        if (a.name.indexOf('data-') !== 0) continue;
        seen[a.name] = true;
        if (target.getAttribute(a.name) !== a.value) {
            target.setAttribute(a.name, a.value);
        }
    }
    var targetAttrs = Array.prototype.slice.call(target.attributes);
    for (i = 0; i < targetAttrs.length; i++) {
        var name = targetAttrs[i].name;
        if (name.indexOf('data-') !== 0) continue;
        if (!seen[name]) target.removeAttribute(name);
    }
}

function needsPolling(listEl) {
    return !!(listEl && listEl.getAttribute('data-poll-needed') === '1');
}

function stopLinesPolling() {
    if (window.__codaLinesPollHandle) {
        clearInterval(window.__codaLinesPollHandle);
        window.__codaLinesPollHandle = null;
    }
}

async function pollLinesTick() {
    var listEl = document.getElementById('coda-lines');
    if (!listEl) { stopLinesPolling(); return; }

    var fragmentUrl = listEl.getAttribute('data-lines-fragment-url');
    if (!fragmentUrl) { stopLinesPolling(); return; }

    try {
        var response = await fetch(fragmentUrl, {
            method: 'GET',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
        });
        if (!response.ok) return;

        var html = await response.text();
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newList = doc.getElementById('coda-lines');
        if (!newList) return;

        syncDataAttrs(listEl, newList);
        listEl.innerHTML = newList.innerHTML;

        reinitFlowbiteWidgets(listEl);
        if (window.htmx) {
            window.htmx.process(listEl);
        }

        if (!needsPolling(listEl)) {
            stopLinesPolling();
        }
    } catch (err) {
        console.error('[coda-detail] poll failed', err);
    }
}

function ensureLinesPolling() {
    var listEl = document.getElementById('coda-lines');
    if (!listEl) return;
    if (!needsPolling(listEl)) return;
    if (window.__codaLinesPollHandle) return;

    // First tick on the next macrotask so the caller can finish any
    // DOM work; then keep ticking at POLL_INTERVAL_MS. See the
    // matching rationale in `coda-upload.js#ensureCodaPolling`.
    setTimeout(pollLinesTick, 0);
    window.__codaLinesPollHandle = setInterval(pollLinesTick, POLL_INTERVAL_MS);
}

// ---------------------------------------------------------------------------
// Line drawer
// ---------------------------------------------------------------------------

function openLineDrawer() {
    var drawer = document.getElementById('coda-line-drawer');
    var backdrop = document.getElementById('coda-line-drawer-backdrop');
    if (!drawer || !backdrop) return;
    drawer.classList.remove('translate-x-full');
    drawer.setAttribute('aria-hidden', 'false');
    backdrop.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
}

function closeLineDrawer() {
    var drawer = document.getElementById('coda-line-drawer');
    var backdrop = document.getElementById('coda-line-drawer-backdrop');
    if (!drawer || !backdrop) return;
    drawer.classList.add('translate-x-full');
    drawer.setAttribute('aria-hidden', 'true');
    backdrop.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
    // Clear the body after the slide-out transition so we don't
    // leave stale content in the DOM (and to make sure a re-open
    // always fetches fresh data — line state might have changed
    // server-side since the last open).
    setTimeout(function() {
        var content = document.getElementById('coda-line-drawer-content');
        if (content) content.innerHTML = '';
    }, 200);
}

function renderLoading(content) {
    content.innerHTML = ''
        + '<div class="flex items-center justify-center py-16">'
        + '<svg class="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">'
        + '<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>'
        + '<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>'
        + '</svg></div>';
}

function renderError(content, message) {
    content.innerHTML = '<div class="text-center py-16 text-red-500 px-4"><p>' + (message || 'Error') + '</p></div>';
}

async function loadLineDrawer(url) {
    var content = document.getElementById('coda-line-drawer-content');
    if (!content) return;
    renderLoading(content);
    openLineDrawer();
    try {
        var response = await fetch(url, {
            method: 'GET',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
        });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        content.innerHTML = await response.text();
        // The drawer body has `hx-post` attributes and Flowbite
        // tooltips (none today but future-proofs P5c additions).
        reinitFlowbiteWidgets(content);
        if (window.htmx) {
            window.htmx.process(content);
        }
    } catch (err) {
        console.error('[coda-detail] drawer load failed', err);
        var root = document.querySelector('[data-auto-init="codaDetail"]');
        var msg = (root && root.getAttribute('data-error-load-line'))
            || 'Could not load the line. Please close and try again.';
        renderError(content, msg);
    }
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

// Document-level listeners (document + document.body) are attached at
// most once per page load. ``_runAutoInit`` can re-call
// ``initCodaDetail`` on every HTMX main-content swap (hx-boost is on
// ``#main-content`` so every in-app navigation lands here), and
// ``document`` / ``document.body`` survive those swaps unchanged —
// re-binding would stack duplicate delegated handlers and the drawer
// would start opening twice per click (or, subtler, a closed-drawer
// handler would fire right after an open one and close it again).
var _codaDetailDocListenersBound = false;

// Per-root listener bookkeeping. A WeakSet is the right tool here:
// ``#main-content`` is swapped via hx-boost on every nav — the
// ``<div data-auto-init="codaDetail">`` wrapper node that comes back
// after a Back navigation is a FRESH element, not the same object we
// bound last time. A data-attribute flag would be preserved in HTMX's
// history snapshot (it serialises outerHTML on cache) and trick the
// next ``initCodaDetail`` call into thinking "already bound", while
// the real ``addEventListener`` is orphaned on the discarded node —
// net effect: row clicks stop opening the drawer after a
// click-invoice-link → Back cycle.
var _boundRoots = new WeakSet();

function _bindDocumentListeners() {
    if (_codaDetailDocListenersBound) return;
    _codaDetailDocListenersBound = true;

    document.addEventListener('click', function(event) {
        if (findClosest(event.target, '[data-coda-line-close]')
            || findClosest(event.target, '[data-coda-line-backdrop]')) {
            closeLineDrawer();
        }
    });

    document.addEventListener('keydown', function(event) {
        if (event.key !== 'Escape') return;
        var drawer = document.getElementById('coda-line-drawer');
        if (drawer && !drawer.classList.contains('translate-x-full')) {
            closeLineDrawer();
        }
    });

    // Server sends `HX-Trigger: closeCodaLineDrawer` after a successful
    // Confirm/Ignore POST. HTMX fires this as a plain event on
    // document.body — we listen and close the drawer. The lines
    // table was already refreshed via `hx-target="#coda-lines"` +
    // `hx-swap="outerHTML"`, which also re-arms our poll hook via
    // `htmx:afterSwap` below.
    document.body.addEventListener('closeCodaLineDrawer', function() {
        closeLineDrawer();
    });

    // Re-arm the poll loop after every HTMX swap that touched
    // #coda-lines (Confirm/Ignore from the drawer hits this path).
    document.body.addEventListener('htmx:afterSwap', function(evt) {
        var target = evt && evt.detail && evt.detail.target;
        if (!target) return;
        ensureLinesPolling();
    });
}

function _bindRowClickListener(root) {
    if (_boundRoots.has(root)) return;
    _boundRoots.add(root);

    root.addEventListener('click', function(event) {
        // Ignore clicks on anchors inside the row (e.g. invoice link
        // in the "Matched invoice" column) — they handle their own
        // navigation via `onclick="event.stopPropagation()"`.
        var opener = findClosest(event.target, '[data-coda-line-open]');
        if (!opener) return;
        // Belt-and-braces: skip if the click originated inside an
        // <a> or <button> that shouldn't open the drawer.
        if (findClosest(event.target, 'a, button, form, input, textarea')) return;
        event.preventDefault();
        var url = opener.getAttribute('data-url');
        if (url) loadLineDrawer(url);
    });
}

export function initCodaDetail() {
    var root = document.querySelector('[data-auto-init="codaDetail"]');
    if (!root) return;

    // HTMX's history cache is an outerHTML snapshot — when the user
    // clicks an invoice link (hx-boost), navigates to the invoice,
    // then hits Back, HTMX restores the cached #main-content exactly
    // as it was at nav-away time, including an open drawer. That
    // drawer is a FRESH DOM node with no JS listeners bound; close
    // it defensively so the restored page starts in a clean state
    // and the user's next row click loads a drawer from the server
    // instead of animating-open a stale snapshot.
    closeLineDrawer();

    stopLinesPolling();
    ensureLinesPolling();

    _bindRowClickListener(root);
    _bindDocumentListeners();
}

export function autoInitCodaDetail() {
    initCodaDetail();
}
